import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

const ShareModal = ({ show, onHide, meetingData, shareType = 'all' }) => {
  const [emailList, setEmailList] = useState('');
  const [message, setMessage] = useState('');
  const [includeTranscript, setIncludeTranscript] = useState(true);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [includeActionItems, setIncludeActionItems] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Function to validate emails
  const validateEmails = (emails) => {
    const emailArr = emails.split(',').map(email => email.trim());
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    const invalidEmails = emailArr.filter(email => !emailRegex.test(email));
    return invalidEmails.length === 0;
  };

  // Function to handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Reset states
    setSuccess(false);
    setError('');
    
    // Validate emails
    if (!emailList) {
      setError('Please enter at least one email address');
      return;
    }
    
    if (!validateEmails(emailList)) {
      setError('One or more email addresses are invalid');
      return;
    }

    // Check if at least one content type is selected
    if (!includeTranscript && !includeSummary && !includeActionItems) {
      setError('Please select at least one content type to share');
      return;
    }
    
    // Simulate sending email
    setIsSending(true);
    
    setTimeout(() => {
      setIsSending(false);
      setSuccess(true);
      
      // Auto close after success
      setTimeout(() => {
        handleReset();
        onHide();
      }, 2000);
    }, 1500);
  };

  // Function to reset form
  const handleReset = () => {
    setEmailList('');
    setMessage('');
    setIncludeTranscript(true);
    setIncludeSummary(true);
    setIncludeActionItems(true);
    setSuccess(false);
    setError('');
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      centered
      contentClassName="bg-dark text-white"
    >
      <Modal.Header closeButton closeVariant="white" className="border-secondary">
        <Modal.Title>Share Meeting Content</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {success ? (
          <Alert variant="success">
            <i className="bi bi-check-circle me-2"></i>
            Content shared successfully!
          </Alert>
        ) : (
          <Form onSubmit={handleSubmit}>
            {error && (
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                {error}
              </Alert>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Meeting</Form.Label>
              <p className="mb-0 text-white">
                <strong>{meetingData?.title || 'Meeting'}</strong>
                {meetingData?.date && (
                  <span className="ms-2 text-white-50">
                    <i className="bi bi-calendar me-1"></i>
                    {meetingData.date}
                  </span>
                )}
              </p>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email Recipients*</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter email addresses (comma separated)"
                value={emailList}
                onChange={(e) => setEmailList(e.target.value)}
                className="bg-dark text-white border-secondary"
                required
              />
              <Form.Text className="text-white-50">
                Multiple email addresses should be separated by commas
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Personal Message (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add a personal message to the email"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="bg-dark text-white border-secondary"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Content to Include</Form.Label>
              <div>
                {shareType === 'all' && (
                  <Form.Check
                    type="checkbox"
                    label="Full Transcript"
                    id="include-transcript"
                    checked={includeTranscript}
                    onChange={() => setIncludeTranscript(!includeTranscript)}
                    className="mb-2 text-white"
                  />
                )}
                
                <Form.Check
                  type="checkbox"
                  label="Meeting Summary"
                  id="include-summary"
                  checked={includeSummary}
                  onChange={() => setIncludeSummary(!includeSummary)}
                  className="mb-2 text-white"
                />
                
                <Form.Check
                  type="checkbox"
                  label="Action Items"
                  id="include-action-items"
                  checked={includeActionItems}
                  onChange={() => setIncludeActionItems(!includeActionItems)}
                  className="mb-2 text-white"
                />
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Delivery Format</Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  name="format"
                  label="PDF Attachment"
                  id="format-pdf"
                  defaultChecked
                  className="mb-2 text-white"
                />
                
                <Form.Check
                  type="radio"
                  name="format"
                  label="Link to View Online"
                  id="format-link"
                  className="mb-2 text-white"
                />
              </div>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="border-secondary">
        {!success && (
          <>
            <Button variant="outline-secondary" onClick={onHide} disabled={isSending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={isSending}>
              {isSending ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Sending...
                </>
              ) : (
                <>Send</>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ShareModal;
